import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useAccessToken } from "@/hooks";
import { CustomerVaultsApi } from "@/openapi/generated";
import type { GetCustomerVaultResponse } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { normalizeVaultDefaults } from "@/features/balance/utils/vault";

interface UseVaultsGetProps {
  userId: number;
  tenantId: number;
}

export const useVaultsGet = (
  { userId, tenantId }: UseVaultsGetProps,
  options?: { enabled?: boolean },
) => {
  const accessToken = useAccessToken();
  const previousAccessTokenRef = useRef<string | undefined>(undefined);

  const { data, isError, isLoading, isSuccess, isPending, isFetching, refetch, error } =
    useQuery<GetCustomerVaultResponse[]>({
      queryKey: [QueryKeys.CustomerVaults, userId],
      queryFn: async () => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }

        const service = createAuthenticatedApi(
          CustomerVaultsApi,
          accessToken,
        );

        const result = await service.apiUsersUserIdVaultsGet({
          userId,
          xTenantID: tenantId,
        });

        return result ?? [];
      },
      select: normalizeVaultDefaults,
      enabled:
        options?.enabled !== false &&
        !!accessToken &&
        !!userId &&
        !!tenantId,
      staleTime: 2 * 60 * 1000,
      // Layout SSR seeds this query. Failed prefetch does not cache [] (see prefetchVaultsGet).
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    const previous = previousAccessTokenRef.current;
    previousAccessTokenRef.current = accessToken;
    if (previous && accessToken && previous !== accessToken) {
      void refetch();
    }
  }, [accessToken, refetch]);

  return {
    data: data ?? [],
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
    error,
  };
};
